import { text, index } from "drizzle-orm/sqlite-core";
import { defineTable } from "@kubdee/sdk/schema";
import { marketingPostTargets } from "./post-targets";

// ============================================================================
// marketing_publish_logs — Append-only audit history per target
//
// Every state transition on a `marketing_post_targets` row appends one row
// here. Drives the "Activity" timeline in the post detail UI and lets ops
// reconstruct what happened when a publish goes wrong.
//
// Read-only by design — there is no update path; rows are only ever
// inserted. The doctype registry marks this as `isReadOnly: true`.
// ============================================================================
export const marketingPublishLogs = defineTable(
  "marketing",
  "publish_logs",
  {
    id: text("id").primaryKey(), // ULID
    companyId: text("company_id").notNull(),
    targetId: text("target_id")
      .notNull()
      .references(() => marketingPostTargets.id, { onDelete: "cascade" }),
    // queued | publishing | published | failed | cancelled | retry
    event: text("event").notNull(),
    providerPostId: text("provider_post_id"),
    providerUrl: text("provider_url"),
    errorCode: text("error_code"),
    errorMessage: text("error_message"),
    // Provider-side request/response snapshot for debugging.
    payloadJson: text("payload_json"),
    createdAt: text("created_at").notNull(),
    createdBy: text("created_by"), // nullable for system events (cron, webhook)
  },
  (table) => [
    index("marketing_publish_logs_company_id_idx").on(table.companyId),
    // Timeline query: WHERE target_id = ? ORDER BY created_at.
    index("marketing_publish_logs_target_created_idx").on(
      table.targetId,
      table.createdAt,
    ),
    // Tenant-wide ops dashboards (#791) — "publish errors in the last hour
    // across the tenant" needs a composite (company_id, created_at) index
    // so the planner doesn't fall back to a full scan.
    index("marketing_publish_logs_company_created_idx").on(
      table.companyId,
      table.createdAt,
    ),
  ],
);

export type MarketingPublishLog = typeof marketingPublishLogs.$inferSelect;
export type NewMarketingPublishLog = typeof marketingPublishLogs.$inferInsert;
